import { ChangeDetectionStrategy, Component, effect, inject, untracked } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { CreateFieldInterventionInformationComponent } from '@connect-services/client/components/create-field-intervention-information/create-field-intervention-information.component';
import { StringifyPipe } from '@connect-services/client/pipes/stringify.pipe';
import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import {
    CustomColumn,
    CustomTableComponent,
    SortState,
} from '@connect-services/src/lib/components/table/table.component';
import { Layer, LayerAttribute, LayersService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CheckboxComponent,
        CreateFieldInterventionInformationComponent,
        CustomTableComponent,
        LoadingComponent,
        RessourcesModule,
        StringifyPipe,
    ],
    selector: 'app-entity-selection-field-intervention-table',
    templateUrl: './field-intervention-entity-selection-table.component.html',
})
export class FieldInterventionEntitySelectionTableComponent {
    public bookmarkedAttributes: string[] = [];
    public customColumns: CustomColumn[] = [];

    protected readonly CheckboxState = CheckboxState;
    protected readonly createFieldInterventionService = inject(CreateFieldInterventionService);

    private readonly layersService = inject(LayersService);

    public constructor() {
        effect(async () => {
            if (!this.createFieldInterventionService.activeLayers().length) {
                return;
            }

            await untracked(() => this.initializeTableWithData());
        });
    }

    public async initializeTableWithData(): Promise<void> {
        this.createFieldInterventionService.isError.set(false);
        //Grouped layers have the same columns, it's check in the back
        await this.prepareTableColumns(this.createFieldInterventionService.activeLayers()[0]);
    }

    public async sort(sortState: SortState | undefined): Promise<void> {
        this.createFieldInterventionService.order.set(sortState?.order);
        this.createFieldInterventionService.orderBy.set(sortState?.order ? sortState.orderBy : '');
        await this.createFieldInterventionService.fetchEntities();
    }

    private createCustomTables(): void {
        this.customColumns = [{ key: 'businessIdentifier', label: 'businessIdentifier' }];

        this.customColumns = [
            ...this.customColumns,
            ...this.bookmarkedAttributes.map((bookmarkAttribute: string): CustomColumn => {
                return { key: bookmarkAttribute, label: bookmarkAttribute };
            }),
        ];
    }

    private async getBookmarkedAttributes(layer: Layer, businessIdentifier: string | null): Promise<LayerAttribute[]> {
        const attributes = await firstValueFrom(
            this.layersService.layerControllerGetAttributes({
                id: layer.id,
            }),
        );
        const filteredAttributes: LayerAttribute[] = [];

        for (const layerAttribute of attributes) {
            if (!layerAttribute.isBookmarked) {
                continue;
            }

            if (layerAttribute.column === businessIdentifier) {
                filteredAttributes.unshift(layerAttribute);
                continue;
            }

            filteredAttributes.push(layerAttribute);
        }

        return filteredAttributes;
    }

    private async prepareTableColumns(layer: Layer): Promise<void> {
        const businessIdentifier = layer.businessIdentifier;
        const layerBookmarks = await this.getBookmarkedAttributes(layer, businessIdentifier);

        this.bookmarkedAttributes = layerBookmarks.map((layerAttribute: LayerAttribute) => layerAttribute.column);
        this.createCustomTables();
    }
}
